/**
 * UserDetail — PR-28 / US-USER-002 surface.
 *
 * Canonical read + role-change pane for a single user. The list page
 * (`pages/Users.tsx`) links each row here; list-only role editing stays
 * available via the Shield modal but this page is the single pane of
 * glass a future audit can link to.
 *
 * Guards:
 *   - `/users/:id` route is AdminGuard-wrapped in App.tsx so only admins reach it.
 *   - Self-role-change is blocked in the UI (disabled select + warning);
 *     the backend `PATCH /api/users/:id/role` enforces the same rule (403).
 *
 * 404 path: GET /api/users/:userId returns 404 when the id is unknown;
 * this page renders a dedicated not-found state instead of forwarding to
 * the global /not-found so the admin stays in-context.
 */

import { useEffect, useState, useCallback } from 'react';
import {
  PageContainer,
  PageShell,
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Input,
  Loading,
} from '@resource-kit/ui/react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, Shield, Loader2 } from 'lucide-react';
import { useToast } from '@components/ui/Toast';
import { useConfirm } from '@components/ui/ConfirmDialog';
import { useBetterAuth } from '@hooks/useAuth';
import { http, ApiError } from '@lib/api';
import { ROLES, ROLE_LABELS, ROLE_DESCRIPTIONS } from '@/permissions';
import { resolveApiError } from '@lib/errors';
import { resendInvitationRequest, revokeInvitationRequest } from '@/services/users';
import { formatDateTime } from '@lib/datetime';

// Mirrors the identity context's `IdentityUserDetail` (openapi:
// IdentityUserSummary + `permissions`). The rebuilt `/api/users/{id}`
// face does NOT expose `updatedAt` / `lastLoggedInAt`, so they are
// optional here and the render guards degrade to "never" / omit when
// absent.
interface UserDetailResponse {
  id: string;
  email: string;
  name: string;
  role: string;
  status: string;
  permissions: string[];
  /**
   * The invitation still in flight (#1737), or null once accepted. Optional
   * on the type so a stale cached payload from before the field existed
   * renders as "no invitation" instead of throwing.
   */
  invitation?: { createdAt: string; expiresAt: string } | null;
  createdAt: string | null;
  updatedAt?: string | null;
  lastLoggedInAt?: string | null;
}

const roleBadgeStyles: Record<string, string> = {
  admin: 'bg-primary/15 text-primary border-primary/20',
  operator: 'bg-accent/15 text-accent border-accent/20',
  viewer: 'bg-secondary text-foreground border-border',
};

function RoleChip({ role }: { role: string }) {
  const { t } = useTranslation();
  const raw = ROLE_LABELS[role] || role;
  return (
    <span
      data-testid="user-role-chip"
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium border ${roleBadgeStyles[role] || roleBadgeStyles.viewer}`}
    >
      {t(raw, { defaultValue: raw })}
    </span>
  );
}

export default function UserDetail() {
  const { id } = useParams<{ id: string }>();
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const toast = useToast();
  const { confirm } = useConfirm();
  const { user: currentUser } = useBetterAuth();

  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [user, setUser] = useState<UserDetailResponse | null>(null);
  const [role, setRole] = useState<string>('');
  const [saving, setSaving] = useState(false);
  const [resending, setResending] = useState(false);
  const [revoking, setRevoking] = useState(false);
  const [error, setError] = useState<string>('');

  const isSelf = !!currentUser && !!user && currentUser.id === user.id;
  // Lapsed is a presentation fact, computed here rather than asked of the
  // backend: the window is a stored timestamp, and a server-side boolean
  // would go stale the moment the page sat open across the expiry.
  const invitationExpired =
    !!user?.invitation && Date.parse(user.invitation.expiresAt) <= Date.now();

  const load = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    setError('');
    try {
      // The api-client unwraps the `{ data }` success envelope, so this
      // resolves directly to the `IdentityUserDetail` payload.
      const payload = await http.get<UserDetailResponse>(`/api/users/${encodeURIComponent(id)}`);
      if (!payload) {
        setNotFound(true);
        return;
      }
      setUser(payload);
      setRole(payload.role);
      setNotFound(false);
    } catch (err) {
      if (err instanceof ApiError && err.status === 404) {
        setNotFound(true);
      } else {
        console.error('[users] detail load failed:', err);
        setError(
          resolveApiError(err, i18n, {
            namespace: 'users',
            fallbackKey: 'users.detail.loadFailed',
          }),
        );
      }
    } finally {
      setLoading(false);
    }
  }, [id, t]);

  useEffect(() => {
    void load();
  }, [load]);

  const handleResend = async () => {
    if (!user) return;
    setResending(true);
    try {
      await resendInvitationRequest(user.id);
      toast.success(t('users.invitationResent'));
      // Re-read: the new ticket carries a new window, and leaving the old
      // "expires" on screen would tell the admin the resend did nothing.
      await load();
    } catch (err) {
      toast.error(
        resolveApiError(err, i18n, {
          namespace: 'users',
          fallbackKey: 'users.inviteUserFailed',
        }),
      );
    } finally {
      setResending(false);
    }
  };

  const handleRevoke = async () => {
    if (!user) return;
    // Irreversible: this deletes the invited account. The confirm names the
    // consequence rather than asking a generic "are you sure" — sitting next
    // to a resend button, the two must not be one misclick apart.
    const yes = await confirm({
      title: t('users.revokeInvitation'),
      message: t('users.revokeInvitationConfirm'),
      type: 'danger',
      confirmText: t('users.revokeInvitation'),
      confirmTestId: 'user-invitation-revoke-confirm',
    });
    if (!yes) return;
    setRevoking(true);
    try {
      await revokeInvitationRequest(user.id);
      toast.success(t('users.invitationRevoked'));
      // The account is gone, so there is nothing left to show — go back to
      // the list rather than leave a detail page for a deleted row.
      navigate('/users');
    } catch (err) {
      setRevoking(false);
      toast.error(
        resolveApiError(err, i18n, {
          namespace: 'users',
          fallbackKey: 'users.revokeInvitationFailed',
        }),
      );
    }
  };

  const handleSave = async () => {
    if (!user) return;
    if (role === user.role) return;
    if (isSelf) return;
    setSaving(true);
    try {
      await http.patch(`/api/users/${encodeURIComponent(user.id)}/role`, { role });
      toast.success(t('users.detail.roleUpdated'));
      await load();
    } catch (err) {
      // Mutation errors surface via toast only — `setError` stays
      // reserved for the `load()` failure path that leaves the page
      // empty and needs a persistent inline banner. Dual-surfacing
      // the same string was redundant noise (Round 2 review).
      const message = resolveApiError(err, i18n, {
        namespace: 'users',
        fallbackKey: 'users.roleUpdateFailed',
      });
      toast.error(t('users.roleUpdateFailed'), message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <PageContainer>
        <Loading />
      </PageContainer>
    );
  }

  if (notFound) {
    return (
      <PageContainer data-testid="user-detail-not-found">
        <Button variant="ghost" size="sm" onClick={() => navigate('/users')} className="gap-1">
          <ArrowLeft className="w-4 h-4" />
          {t('users.detail.backToList')}
        </Button>
        <Card>
          <CardContent className="py-12 text-center">
            <h2 className="text-lg font-medium mb-2">{t('users.detail.notFoundTitle')}</h2>
            <p className="text-sm text-muted-foreground">{t('users.detail.notFoundDesc')}</p>
          </CardContent>
        </Card>
      </PageContainer>
    );
  }

  if (!user) {
    return null;
  }

  return (
    <PageShell title={user.name || user.email} titleAdornment={<RoleChip role={user.role} />}>
      {error && (
        <div className="rounded-md bg-destructive/10 border border-destructive/20 px-4 py-2 text-destructive text-sm">
          {error}
        </div>
      )}

      <Card data-testid="user-detail-page">
        <CardHeader>
          <CardTitle className="text-base">{t('users.detail.profileTitle')}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-1">{t('users.emailLabel')}</label>
            <Input
              data-testid="user-detail-email"
              value={user.email}
              readOnly
              className="bg-muted"
              aria-readonly="true"
            />
            <p className="mt-1 text-xs text-muted-foreground">{t('users.detail.emailHint')}</p>
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">{t('users.nameLabel')}</label>
            <Input
              data-testid="user-detail-name"
              value={user.name || ''}
              readOnly
              className="bg-muted"
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">
              {t('users.detail.statusLabel')}
            </label>
            <Badge variant="secondary" className="capitalize">
              {user.status}
            </Badge>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <Shield className="w-4 h-4" />
            {t('users.detail.roleSectionTitle')}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <label className="block text-sm font-medium">{t('users.permRoleLabel')}</label>
          <select
            data-testid="user-role-select"
            value={role}
            disabled={isSelf || saving}
            onChange={(e) => setRole(e.target.value)}
            className="w-full max-w-xs h-10 rounded-md border border-input bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-60"
          >
            {ROLES.map((r) => (
              <option key={r} value={r}>
                {t(ROLE_LABELS[r], { defaultValue: ROLE_LABELS[r] })}
              </option>
            ))}
          </select>
          <p className="text-xs text-muted-foreground">
            {t(ROLE_DESCRIPTIONS[role] || ROLE_DESCRIPTIONS[user.role], {
              defaultValue: ROLE_DESCRIPTIONS[role] || ROLE_DESCRIPTIONS[user.role],
            })}
          </p>
          {isSelf && (
            <p data-testid="self-demote-warning" className="text-xs text-destructive">
              {t('users.error.self_role_change_forbidden')}
            </p>
          )}
          <div>
            <Button
              data-testid="user-role-save"
              onClick={handleSave}
              disabled={isSelf || saving || role === user.role}
            >
              {saving ? (
                <span className="inline-flex items-center gap-2">
                  <Loader2 className="w-3 h-3 animate-spin" />
                  {t('profile.saving')}
                </span>
              ) : (
                t('users.detail.saveRole')
              )}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* A pending account IS an invitation in flight, so say when it lapses
          and offer a fresh one (#1737). Driven by the `invitation` block, not
          by `status` alone: an accepted account has nothing left to resend. */}
      {user.invitation && (
        <Card data-testid="user-invitation-section">
          <CardHeader>
            <CardTitle className="text-base">{t('users.invitation.title')}</CardTitle>
          </CardHeader>
          <CardContent className="flex flex-wrap items-center gap-x-6 gap-y-2">
            <span className="text-sm text-muted-foreground">
              {t('users.invitation.sentAt')}: {formatDateTime(user.invitation.createdAt)}
            </span>
            <span className="text-sm text-muted-foreground">
              {t('users.invitation.expiresAt')}: {formatDateTime(user.invitation.expiresAt)}
            </span>
            {invitationExpired && (
              <Badge variant="destructive" data-testid="user-invitation-expired">
                {t('users.invitation.expired')}
              </Badge>
            )}
            <Button
              variant="outline"
              size="sm"
              className="ml-auto"
              disabled={resending}
              data-testid="user-invitation-resend"
              onClick={() => void handleResend()}
            >
              {resending ? (
                <span className="inline-flex items-center gap-2">
                  <Loader2 className="w-3 h-3 animate-spin" />
                  {t('users.resendInvitation')}
                </span>
              ) : (
                t('users.resendInvitation')
              )}
            </Button>
            <Button
              variant="outline"
              size="sm"
              disabled={revoking}
              data-testid="user-invitation-revoke"
              className="text-destructive hover:text-destructive"
              onClick={() => void handleRevoke()}
            >
              {t('users.revokeInvitation')}
            </Button>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader>
          <CardTitle className="text-base">{t('users.detail.permissionsTitle')}</CardTitle>
        </CardHeader>
        <CardContent>
          {user.permissions.length === 0 ? (
            <p className="text-sm text-muted-foreground" data-testid="user-permissions-empty">
              {t('users.detail.permissionsEmpty')}
            </p>
          ) : (
            <ul className="flex flex-wrap gap-2" data-testid="user-permissions-list">
              {user.permissions.map((p) => (
                <li key={p}>
                  <Badge variant="secondary" className="text-xs font-mono">
                    {p}
                  </Badge>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>

      <div className="text-xs text-muted-foreground flex gap-4">
        <span>
          {t('users.detail.lastLogin')}:{' '}
          {user.lastLoggedInAt
            ? formatDateTime(user.lastLoggedInAt)
            : t('users.detail.lastLoginNever')}
        </span>
        {user.createdAt && (
          <span>
            {t('users.detail.createdAt')}: {formatDateTime(user.createdAt)}
          </span>
        )}
      </div>

      <div className="pt-2">
        <Link to="/users" className="text-sm text-primary hover:underline">
          {t('users.detail.backToList')}
        </Link>
      </div>
    </PageShell>
  );
}
